import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { updateGameInfo } from "@/lib/stores/unifiedUserStore";
import SaveBar from "./SaveBar";
import CreateGameModal from "./CreateGameModal";
import DeleteGameModal from "./DeleteGameModal";
import ArchiveGameModal from "./ArchiveGameModal";
import { Archive } from "lucide-react";
import { Game } from "@/types-games";
import { getLanguageName, getLanguageFlag } from "@/lib/dataSources";
import { MultiSelectDropdownWithField } from   "@/components/ui/dropdowns/MultiSelectDropdownWithField";
import { Input } from   "@/components/ui/InputDefault";
import { Button } from   "@/components/ui/button";
import { getScriptOptions } from "@/lib/dataSources/scriptTypes";
import { useRTL } from "@/providers/RTLProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
type GamesSetupSectionProps = Record<string, never>;

export default function GamesSetupSection(_props: GamesSetupSectionProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { games, selectedGame, selectedCompany, getCompanyGames } = useSimplifiedUserData();
  const { currentScript, scriptMessage } = useRTL();
  
  // Filter games to only show those from the current company
  const companyGames = useMemo(
    () => selectedCompany ? getCompanyGames(selectedCompany.id) : [],
    [selectedCompany, getCompanyGames]
  );
  
  // Local game selection for settings editing (separate from global platform game)
  const [localSelectedGameId, setLocalSelectedGameId] = useState("");
  
  // Current values
  const [gameName, setGameName] = useState("");
  const [gameDescription, setGameDescription] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [deadline, setDeadline] = useState("");
  const [sections, setSections] = useState<string[]>([]);
  const [engine, setEngine] = useState("");
  const [codeLanguage, setCodeLanguage] = useState("");
  const [scriptType, setScriptType] = useState("");

  const availableScripts = getScriptOptions();
  
  // Original values for change tracking
  const [originalValues, setOriginalValues] = useState({
    localSelectedGameId: "",
    gameName: "",
    gameDescription: "",
    defaultLanguage: "",
    languages: [] as string[],
    deadline: "",
    sections: [] as string[],
    engine: "",
    codeLanguage: "",
    scriptType: ""
  });

  // Create game modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Delete game modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Archive game modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  // Available options - using all languages from the application
  const availableLanguages = [
    'en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar', 'hi', 'nl', 'sv', 'no', 'da', 'fi', 'pl', 'tr', 'he', 'th', 'vi', 'id', 'ms', 'tl', 'el', 'hu', 'cs', 'sk', 'ro', 'bg', 'hr', 'sl', 'et', 'lv', 'lt', 'mt', 'ga', 'cy', 'is', 'fo', 'sq', 'mk', 'sr', 'bs', 'me', 'uk', 'be', 'kk', 'ky', 'uz', 'tg', 'mn', 'ka', 'hy', 'az', 'fa', 'ur', 'bn', 'ta', 'te', 'kn', 'ml', 'gu', 'pa', 'or', 'as', 'ne', 'si', 'my', 'km', 'lo', 'am', 'sw', 'zu', 'af', 'xh', 'yo', 'ig', 'ha', 'so', 'rw', 'lg', 'ak', 'tw', 'ee'
  ];
  
  // Convert languages to format expected by MultiSelectDropdownWithField
  const languageOptions = availableLanguages.map(code => ({
    id: code,
    label: `${getLanguageFlag(code)} ${getLanguageName(code)}`,
    description: code
  }));
  
  const availableSections = ["dialogue", "ui", "subtitles", "audio", "menus", "tutorial"];
  
  // Available game engines
  const availableEngines = [
    { value: "unity", label: "Unity" },
    { value: "unreal", label: "Unreal Engine" },
    { value: "godot", label: "Godot" },
    { value: "custom", label: "Custom Engine" },
    { value: "cryengine", label: "CryEngine" },
    { value: "roblox", label: "Roblox" },
    { value: "fortnite", label: "Fortnite" },
    { value: "meta-horizon", label: "Meta Horizon" },
    { value: "web-gl", label: "Web-GL" },
    { value: "other", label: "Other" }
  ];
  
  // Available code languages
  const availableCodeLanguages = [
    { value: "csharp", label: "C#" },
    { value: "cpp", label: "C++" },
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "TypeScript" },
    { value: "python", label: "Python" },
    { value: "gdscript", label: "GDScript" },
    { value: "lua", label: "Lua" },
    { value: "other", label: "Other" }
  ];

  // Initialize with the current global game when component mounts
  useEffect(() => {
    if (selectedGame && !localSelectedGameId) {
      setLocalSelectedGameId(selectedGame.id);
      loadGameData(selectedGame);
    }
  }, [selectedGame, localSelectedGameId]);

  // Load game data when local game selection changes
  useEffect(() => {
    if (localSelectedGameId && companyGames.length > 0) {
      const game = companyGames.find(g => g.id === localSelectedGameId);
      if (game) {
        loadGameData(game);
        // No longer calling selectGame(game) - we keep global game unchanged
      }
    }
  }, [localSelectedGameId, companyGames]);

  const loadGameData = (game: Game) => {
    const settings = game.settings || {
      languages: [],
      deadline: '',
      sections: [],
      defaultLanguage: '',
      engine: '',
      codeLanguage: '',
      scriptType: ''
    };

    // Format deadline to ensure it's in YYYY-MM-DD format for HTML date input
    let formattedDeadline = settings.deadline || "";
    if (formattedDeadline) {
      // If the deadline contains time information, extract just the date part
      if (formattedDeadline.includes('T')) {
        formattedDeadline = formattedDeadline.split('T')[0];
      }
      // Ensure the date is in YYYY-MM-DD format
      const date = new Date(formattedDeadline);
      if (!isNaN(date.getTime())) {
        formattedDeadline = date.toISOString().split('T')[0];
      }
    }

    setGameName(game.name || "");
    setGameDescription(game.description || "");
    setDefaultLanguage(settings.defaultLanguage || "");
    setLanguages(settings.languages || []);
    setDeadline(formattedDeadline);
    setSections(settings.sections || []);
    setEngine(settings.engine || "");
    setCodeLanguage(settings.codeLanguage || "");
    setScriptType(game.settings?.scriptType || "");
   
      setOriginalValues({
      localSelectedGameId: game.id,
      gameName: game.name || "",
      gameDescription: game.description || "",
      defaultLanguage: settings.defaultLanguage || "",
      languages: settings.languages || [],
      deadline: formattedDeadline,
      sections: settings.sections || [],
      engine: settings.engine || "",
      codeLanguage: settings.codeLanguage || "",
      scriptType: game.settings?.scriptType || ""
    });

 
  };

  // Check if there are any changes
  const hasChanges = 
    localSelectedGameId !== originalValues.localSelectedGameId ||
    gameName !== originalValues.gameName ||
    gameDescription !== originalValues.gameDescription ||
    defaultLanguage !== originalValues.defaultLanguage ||
    JSON.stringify(languages.sort()) !== JSON.stringify(originalValues.languages.sort()) ||
    deadline !== originalValues.deadline ||
    JSON.stringify(sections.sort()) !== JSON.stringify(originalValues.sections.sort()) ||
    engine !== originalValues.engine ||
    codeLanguage !== originalValues.codeLanguage ||
    scriptType !== originalValues.scriptType;

  // Save changes function
  const handleSave = async () => {
    if (!localSelectedGameId) {
      toast({
        title: "Error",
        description: "Please select a game first",
        variant: "destructive",
      });
      return;
    }

    const currentGame = companyGames.find(g => g.id === localSelectedGameId);
    if (!currentGame) {
      toast({
        title: "Error",
        description: "Selected game not found",
        variant: "destructive",
      });
      return;
    }

    try {
      // Prepare the updated game data
      const updates = {
        name: gameName,
        description: gameDescription,
        settings: {
          defaultLanguage,
          languages,
          deadline,
          sections,
          engine,
          codeLanguage,
          scriptType
        }
      };

      // Update the game through the API
      const updatedGame = await updateGameInfo(localSelectedGameId, updates, toast);
      
      // No longer updating global game context - settings are local to this section

      // Update original values to match current values
      setOriginalValues({
        localSelectedGameId,
        gameName,
        gameDescription,
        defaultLanguage,
        languages: [...languages],
        deadline,
        sections: [...sections],
        engine,
        codeLanguage,
        scriptType
      });

      toast({
        title: "Success",
        description: "Game settings saved successfully",
      });

    } catch (error) {
      console.error("Error saving game settings:", error);
      // Error handling is done in updateGameInfo
    }
  };

  // Cancel changes function
  const handleCancel = () => {
    setLocalSelectedGameId(originalValues.localSelectedGameId);
    setGameName(originalValues.gameName);
    setGameDescription(originalValues.gameDescription);
    setDefaultLanguage(originalValues.defaultLanguage);
    setLanguages([...originalValues.languages]);
    setDeadline(originalValues.deadline);
    setSections([...originalValues.sections]);
    setEngine(originalValues.engine);
    setCodeLanguage(originalValues.codeLanguage);
    setScriptType(originalValues.scriptType);
  };

  // Handle language selection from MultiSelectDropdown
  const handleLanguageChange = (selectedLanguages: string[]) => {
    setLanguages(selectedLanguages);
    
    if (selectedLanguages.length === 1) {
      setDefaultLanguage(selectedLanguages[0]);
    } else if (defaultLanguage && !selectedLanguages.includes(defaultLanguage)) {
      setDefaultLanguage("");
    }
  };

  // Handle section selection
  const handleSectionToggle = (section: string) => {
    setSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Handle game creation completion
  const handleGameCreated = (gameId: string) => {
    setLocalSelectedGameId(gameId);
  };

  // Handle game deletion completion
  const handleGameDeleted = () => {
    // Clear the local selection since the game no longer exists
    setLocalSelectedGameId("");
  };

  // Handle game archive completion
  const handleGameArchived = () => {
    // Clear the local selection since the game no longer exists
    setLocalSelectedGameId("");
  };

  return (
    <div>
      <div className="max-w-2xl space-y-6">
     

        {/* Game Selection and Create Button */}
        <div className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label htmlFor="game-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Game to Configure
              </label>
              <select
                id="game-select"
                value={localSelectedGameId}
                onChange={(e) => setLocalSelectedGameId(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded  bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('platform.settings.gamessetupsection.choose_a_game')}</option>
                {companyGames.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 shrink-0"
            >
              Create New Game
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Select which game's settings you want to configure. This won't change your active project.
          </p>
        </div>

        {/* Game Settings - Only show if a game is selected */}
        {localSelectedGameId && (
          <>
            {/* Game Name */}
            <div>
              <label htmlFor="game-name" className="block text-sm font-medium text-gray-700 mb-2">
                Game Name
              </label>
              <Input
                type="text"
                id="game-name"
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                placeholder={t('platform.settings.gamessetupsection.enter_the_game_name')}
                className="w-full"
              />
            </div>

            {/* Game Description */}
            <div>
              <label htmlFor="game-description" className="block text-sm font-medium text-gray-700 mb-2">
                Game Description
              </label>
              <textarea
                id="game-description"
                value={gameDescription}
                onChange={(e) => setGameDescription(e.target.value)}
                placeholder={t('platform.settings.gamessetupsection.enter_a_description_for_the_game')}
                rows={3}
                className="w-full px-3 py-2 border border-input rounded  bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

                         {/* Default Language */}
             <div>
               <label htmlFor="default-language" className="block text-sm font-medium text-gray-700 mb-2">
                 Default Language
               </label>
               <select
                 id="default-language"
                 value={defaultLanguage}
                 onChange={(e) => setDefaultLanguage(e.target.value)}
                 className="w-full px-3 py-2 border border-input rounded  bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                 disabled={languages.length === 0}
               >
                 <option value="">
                   {languages.length === 0 ? "Select supported languages first..." : "Select default language..."}
                 </option>
                 {languageOptions
                   .filter(lang => languages.includes(lang.id))
                   .map((lang) => (
                     <option key={lang.id} value={lang.id}>
                       {lang.label}
                     </option>
                   ))}
               </select>
               <p className="mt-1 text-sm text-gray-500">
                 The primary language for this game. Must be selected from supported languages.
               </p>
             </div>

                         {/* Supported Languages */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Supported Languages
               </label>
               <MultiSelectDropdownWithField
                 placeholder={t('platform.settings.gamessetupsection.select_languages')}
                 options={languageOptions}
                 value={languages}
                 onChange={handleLanguageChange}
                 multiple={true}
                 showSelectedOptions={true}
               />
               <p className="mt-1 text-sm text-gray-500">
                 Select all languages that will be supported in this game.
               </p>
             </div>

            {/* Project Deadline */}
            <div>
              <label htmlFor="deadline" className="block text-sm font-medium text-foreground mb-2">
                Project Deadline
              </label>
              <Input
                type="date"
                id="deadline"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full"
              />
              <p className="mt-1 text-sm text-muted-foreground">
                Target completion date for the localization project.
              </p>
            </div>

            {/* Game Engine */}
            <div>
              <label htmlFor="engine" className="block text-sm font-medium text-foreground mb-2">
                Game Engine
              </label>
              <select
                id="engine"
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('platform.settings.gamessetupsection.select_an_engine')}</option>
                {availableEngines.map((eng) => (
                  <option key={eng.value} value={eng.value}>
                    {eng.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-muted-foreground">
                The game engine used for this project.
              </p>
            </div>

            {/* Code Language */}
            <div>
              <label htmlFor="codeLanguage" className="block text-sm font-medium text-foreground mb-2">
                Code Language
              </label>
              <select
                id="codeLanguage"
                value={codeLanguage}
                onChange={(e) => setCodeLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('platform.settings.gamessetupsection.select_a_language')}</option>
                {availableCodeLanguages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-muted-foreground">
                The programming language used in your game code.
              </p>
            </div>

            {/* Script Type / Writing System */}
            <div>
              <label htmlFor="game-script-type" className="block text-sm font-medium text-foreground mb-2">
                Script / Writing System
              </label>
              <select
                id="game-script-type"
                value={scriptType}
                onChange={(e) => setScriptType(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('platform.settings.gamessetupsection.use_companyuser_default')}</option>
                {availableScripts.map(script => (
                  <option key={script.value} value={script.value}>
                    {script.label} ({script.direction.toUpperCase()})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-muted-foreground">
                Override the script type for this specific game. This will take priority over company and user settings.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Current: {scriptMessage}
              </p>
            </div>

            {/* Archive Game Section */}
            <div className="mt-8 pt-8 border-t border-amber-500/20">
              <h3 className="text-lg font-semibold text-amber-600 mb-2 flex items-center gap-2">
                <Archive className="h-5 w-5" />
                Archive Game
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Archive this game to safely store all its data. The game will be removed but can be restored later from the Management Portal.
              </p>
              <Button
                variant="outline"
                onClick={() => setIsArchiveModalOpen(true)}
                className="w-full sm:w-auto border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive Game
              </Button>
            </div>

            {/* Delete Game Section */}
            <div className="mt-8 pt-8 border-t border-destructive/20">
              <h3 className="text-lg font-semibold text-destructive mb-2">{t('platform.settings.gamessetupsection.danger_zone')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Once you delete a game, there is no going back. Please be certain.
              </p>
              <Button
                variant="destructive"
                onClick={() => setIsDeleteModalOpen(true)}
                className="w-full sm:w-auto"
              >
                Delete Game
              </Button>
            </div>
          
          </>
        )}

        {/* No Game Selected Message */}
        {!localSelectedGameId && companyGames.length > 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('platform.settings.gamessetupsection.please_select_a_game_to_configure_its_settings')}</p>
          </div>
        )}

        {/* No Games Available Message */}
        {companyGames.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('platform.settings.gamessetupsection.no_games_available_in_this_company_create_a_new_game_or_contact_your_administrator')}</p>
          </div>
        )}
      </div>
      
      {/* Save Bar */}
      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />

      {/* Create Game Modal */}
      <CreateGameModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onGameCreated={handleGameCreated}
      />

      {/* Delete Game Modal */}
      {localSelectedGameId && (
        <DeleteGameModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          gameId={localSelectedGameId}
          gameName={gameName}
          onGameDeleted={handleGameDeleted}
        />
      )}

      {/* Archive Game Modal */}
      {localSelectedGameId && selectedCompany && (
        <ArchiveGameModal
          isOpen={isArchiveModalOpen}
          onClose={() => setIsArchiveModalOpen(false)}
          gameId={localSelectedGameId}
          gameName={gameName}
          companyName={selectedCompany.name}
          onGameArchived={handleGameArchived}
        />
      )}
    </div>
  );
}
